/**
 * ScoreHeader Component
 * 
 * Displays the four key alignment scores at the top of the MarketFit Analysis page:
 * - Deal Score (primary)
 * - Product-Situation Alignment
 * - Product-Situation Value
 * - Actor-Situation Alignment
 */

import React from 'react';
import { Progress, Tooltip } from 'antd';
import {
  AimOutlined,
  LinkOutlined,
  DollarOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { AlignmentScores } from '../../types/marketFit';
import styles from './ScoreHeader.module.css';

interface ScoreHeaderProps {
  scores: AlignmentScores;
  isLoading?: boolean;
}

const getScoreColor = (score: number, isPercentage: boolean = true): string => {
  const threshold = isPercentage ? score * 100 : score;
  if (threshold >= 70) return '#22c55e';
  if (threshold >= 40) return '#fbbf24';
  return '#ef4444';
};

const getScoreClass = (score: number, isPercentage: boolean = true): string => {
  const threshold = isPercentage ? score * 100 : score;
  if (threshold >= 70) return styles.scoreValueGreen;
  if (threshold >= 40) return styles.scoreValueYellow;
  return styles.scoreValueRed;
};

const formatPercentage = (value: number): string => {
  return `${Math.round(value * 100)}`;
};

const formatValue = (value: number): string => {
  if (value >= 100) return value.toFixed(0);
  if (value >= 10) return value.toFixed(1);
  return value.toFixed(2);
};

export const ScoreHeader: React.FC<ScoreHeaderProps> = ({ 
  scores,
  isLoading = false,
}) => {
  const {
    dealScore,
    productSituationAlignment,
    productSituationValue,
    actorSituationAlignment,
  } = scores;

  return (
    <div className={styles.scoreHeader}>
      {/* Deal Score - Primary metric */}
      <Tooltip
        title={
          <div className={styles.tooltip}>
            <div className={styles.tooltipTitle}>Deal Score</div>
            <div className={styles.tooltipContent}>
              Composite score calculated as:<br/>
              Alignment × Value × Actor = {formatPercentage(productSituationAlignment)}% × {formatValue(productSituationValue)} × {formatPercentage(actorSituationAlignment)}%
            </div>
          </div>
        }
      >
        <div className={`${styles.scoreCard} ${styles.scoreCardPrimary}`}>
          <div className={styles.scoreCardIcon}>
            <AimOutlined />
          </div>
          <div className={styles.scoreLabel}>Deal Score</div>
          <div className={`${styles.scoreValue} ${getScoreClass(dealScore)}`}>
            {isLoading ? '—' : formatValue(dealScore)}
          </div>
          <Progress
            percent={Math.min(dealScore * 100, 100)}
            showInfo={false}
            strokeColor={getScoreColor(dealScore)}
            trailColor="rgba(255, 255, 255, 0.1)"
            className={styles.scoreProgress}
          />
        </div>
      </Tooltip>

      {/* Product-Situation Alignment */}
      <Tooltip
        title={
          <div className={styles.tooltip}>
            <div className={styles.tooltipTitle}>Product-Situation Alignment</div>
            <div className={styles.tooltipContent}>
              How well the product inputs match available resources and benefits match customer needs.<br/>
              Input Alignment × Benefit Alignment
            </div>
          </div>
        }
      >
        <div className={styles.scoreCard}>
          <div className={styles.scoreCardIcon}>
            <LinkOutlined />
          </div>
          <div className={styles.scoreLabel}>Alignment</div>
          <div className={`${styles.scoreValue} ${getScoreClass(productSituationAlignment)}`}>
            {isLoading ? '—' : formatPercentage(productSituationAlignment)}
            <span className={styles.scoreUnit}>%</span>
          </div>
          <Progress
            percent={productSituationAlignment * 100}
            showInfo={false}
            strokeColor={getScoreColor(productSituationAlignment)}
            trailColor="rgba(255, 255, 255, 0.1)"
            className={styles.scoreProgress}
          />
        </div>
      </Tooltip>

      {/* Product-Situation Value */}
      <Tooltip
        title={
          <div className={styles.tooltip}>
            <div className={styles.tooltipTitle}>Product-Situation Value</div>
            <div className={styles.tooltipContent}>
              Value ratio: Value Generated / Total Cost.<br/>
              Higher is better. A ratio {'>'} 1 means positive ROI.
            </div>
          </div>
        }
      >
        <div className={styles.scoreCard}>
          <div className={styles.scoreCardIcon}>
            <DollarOutlined />
          </div>
          <div className={styles.scoreLabel}>Value Ratio</div>
          <div className={`${styles.scoreValue} ${getScoreClass(productSituationValue, false)}`}>
            {isLoading ? '—' : formatValue(productSituationValue)}
            <span className={styles.scoreUnit}>x</span>
          </div>
          <div className={styles.valueIndicator}>
            {productSituationValue >= 1 ? (
              <span className={styles.valuePositive}>Positive ROI</span>
            ) : (
              <span className={styles.valueNegative}>Below Break-even</span>
            )}
          </div>
        </div>
      </Tooltip>

      {/* Actor-Situation Alignment */}
      <Tooltip
        title={
          <div className={styles.tooltip}>
            <div className={styles.tooltipTitle}>Actor-Situation Alignment</div>
            <div className={styles.tooltipContent}>
              How well the decision-maker's priorities and motivations align with addressing this situation.<br/>
              Subjectively scored based on actor priorities vs situation needs.
            </div>
          </div>
        }
      >
        <div className={styles.scoreCard}>
          <div className={styles.scoreCardIcon}>
            <UserOutlined />
          </div>
          <div className={styles.scoreLabel}>Actor Alignment</div>
          <div className={`${styles.scoreValue} ${getScoreClass(actorSituationAlignment)}`}>
            {isLoading ? '—' : formatPercentage(actorSituationAlignment)}
            <span className={styles.scoreUnit}>%</span>
          </div>
          <Progress
            percent={actorSituationAlignment * 100}
            showInfo={false}
            strokeColor={getScoreColor(actorSituationAlignment)}
            trailColor="rgba(255, 255, 255, 0.1)"
            className={styles.scoreProgress}
          />
        </div>
      </Tooltip>
    </div>
  );
};

export default ScoreHeader;
